import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { api } from '../services/api';

interface AuthUser {
  id: number;
  email: string;
  display_name: string;
  role: string;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const TOKEN_KEY = 'onisland_token';
const USER_KEY = 'onisland_user';

function loadFromStorage(): { token: string | null; user: AuthUser | null } {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const user = localStorage.getItem(USER_KEY);
    return { token, user: user ? JSON.parse(user) : null };
  } catch {
    return { token: null, user: null };
  }
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState(() => loadFromStorage());

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await api.auth.login(email, password);
    if (error) throw new Error(error);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setState({ token: data.token, user: data.user });
  }, []);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    const { data, error } = await api.auth.register(email, password, displayName);
    if (error) throw new Error(error);
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setState({ token: data.token, user: data.user });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setState({ token: null, user: null });
  }, []);

  const clearError = useCallback(() => {}, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      isAuthenticated: !!state.token,
      isLoading: false,
      error: null,
      login,
      register,
      logout,
      clearError,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
